import React, { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Ban, CalendarCheck, Copy, BookOpenText, Pencil, Plus, RotateCcw, SearchX, Send, Trash2 } from 'lucide-react'
import {
  Button,
  Card,
  CardToolbar,
  DataTable,
  Dialog,
  EmptyState,
  Entity,
  FilterBar,
  Menu,
  Page,
  Pagination,
  Select,
  Sheet,
  TextArea,
  TextField,
  type DataTableColumn,
  type MenuItem,
} from '@/components/ui'
import {
  useAcdmcOptions,
  useDeleteLessonPlan,
  useLessonPlanStatus,
  useLessonPlans,
  useSaveLessonPlan,
} from '@/hooks/useAcdmc'
import acdmcService, { type LessonPlan, type LessonPlanStatus } from '@/services/acdmcService'
import {
  ClassContextFields,
  READ_ONLY_NOTE,
  StatusBadge,
  TopicPicker,
  allSubjectNames,
  classLabel,
  classSelectOptions,
  errorMessage,
  sectionSelectOptions,
  shortDate,
  todayKey,
  useCanDeleteAcademics,
  useCanWriteAcademics,
  type ClassContextErrors,
} from '@/components/acdmc/AcdmcUi'
import {
  useDuplicateLessonPlan,
  useLessonPlanContext,
  useLessonPlanningDashboard,
} from '@/services/acdmcLessonPlanningService'
import '@/components/acdmc/acdmc.css'

type Draft = {
  _id?: string
  status: LessonPlanStatus
  title: string
  teacherId: string
  teacherName: string
  className: string
  section: string
  subjectName: string
  curriculumId: string
  topicIds: string[]
  topic: string
  date: string
  periods: number
  objectives: string
  activities: string
  resources: string
  assessment: string
  structured: {
    teachingProcess: { introduction: string; summary: string; methodology: string }
    differentiated: { support: string; extension: string }
    homework: { tasks: string; followUp: string }
    reflection: { anticipatedDifficulties: string; nextLesson: string }
  }
  isSupplementary: boolean
  sequenceIndex: number
  sequenceTotal: number
}

const emptyDraft = (): Draft => ({
  status: 'draft',
  title: '',
  teacherId: '',
  teacherName: '',
  className: '',
  section: '',
  subjectName: '',
  curriculumId: '',
  topicIds: [],
  topic: '',
  date: todayKey(),
  periods: 1,
  objectives: '',
  activities: '',
  resources: '',
  assessment: '',
  structured: {
    teachingProcess: { introduction: '', summary: '', methodology: '' },
    differentiated: { support: '', extension: '' },
    homework: { tasks: '', followUp: '' },
    reflection: { anticipatedDifficulties: '', nextLesson: '' },
  },
  isSupplementary: false,
  sequenceIndex: 1,
  sequenceTotal: 1,
})

const toDraft = (plan: LessonPlan): Draft => ({
  _id: plan._id,
  status: plan.status,
  title: plan.title || '',
  teacherId: plan.teacherId || '',
  teacherName: plan.teacherName || '',
  className: plan.className || '',
  section: plan.section || '',
  subjectName: plan.subjectName || '',
  curriculumId: plan.curriculumId || '',
  topicIds: plan.topicIds || [],
  topic: plan.topic || '',
  date: plan.date || todayKey(),
  periods: plan.periods || 1,
  objectives: plan.objectives || '',
  activities: plan.activities || '',
  resources: plan.resources || '',
  assessment: plan.assessment || '',
  structured: {
    teachingProcess: {
      introduction: String(plan.structured?.teachingProcess?.introduction || ''),
      summary: String(plan.structured?.teachingProcess?.summary || ''),
      methodology: String(plan.structured?.teachingProcess?.methodology || ''),
    },
    differentiated: {
      support: String(plan.structured?.differentiated?.support || ''),
      extension: String(plan.structured?.differentiated?.extension || ''),
    },
    homework: {
      tasks: String(plan.structured?.homework?.tasks || ''),
      followUp: String(plan.structured?.homework?.followUp || ''),
    },
    reflection: {
      anticipatedDifficulties: String(plan.structured?.reflection?.anticipatedDifficulties || ''),
      nextLesson: String(plan.structured?.reflection?.nextLesson || ''),
    },
  },
  isSupplementary: Boolean(plan.isSupplementary),
  sequenceIndex: plan.sequencePart?.index || 1,
  sequenceTotal: plan.sequencePart?.total || 1,
})

type When = '' | 'today' | 'week' | 'upcoming' | 'past'

const rangeFor = (when: When) => {
  const today = todayKey()
  if (when === 'today') return { from: today, to: today }
  if (when === 'upcoming') return { from: today, to: '' }
  if (when === 'past') {
    const d = new Date()
    d.setDate(d.getDate() - 1)
    const y = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    return { from: '', to: y }
  }
  if (when === 'week') {
    const d = new Date()
    const start = new Date(d)
    start.setDate(d.getDate() - ((d.getDay() + 6) % 7))
    const end = new Date(start)
    end.setDate(start.getDate() + 6)
    const key = (x: Date) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`
    return { from: key(start), to: key(end) }
  }
  return { from: '', to: '' }
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const AcdmcLessonPlan: React.FC = () => {
  const canDelete = useCanDeleteAcademics('classwork')
  const canWrite = useCanWriteAcademics('classwork')
  const [searchParams, setSearchParams] = useSearchParams()
  const [q, setQ] = useState('')
  const [status, setStatus] = useState<LessonPlanStatus | ''>('')
  const [className, setClassName] = useState('')
  const [section, setSection] = useState('')
  const [subjectName, setSubjectName] = useState('')
  const [teacherId, setTeacherId] = useState('')
  const [when, setWhen] = useState<When>('')
  const [page, setPage] = useState(1)

  const [sheetOpen, setSheetOpen] = useState(false)
  const [draft, setDraft] = useState<Draft>(emptyDraft)
  const [errors, setErrors] = useState<ClassContextErrors & { title?: string; date?: string }>({})
  const [deliverTarget, setDeliverTarget] = useState<LessonPlan | null>(null)
  const [deliveryNote, setDeliveryNote] = useState('')
  const [deliveredOn, setDeliveredOn] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<LessonPlan | null>(null)

  const { data: lpDashboard } = useLessonPlanningDashboard()
  const duplicatePlan = useDuplicateLessonPlan()
  const planContext = useLessonPlanContext(Boolean(sheetOpen), {
    className: draft.className,
    section: draft.section,
    subjectName: draft.subjectName,
    from: draft.date,
    to: draft.date,
  })

  const { data: options } = useAcdmcOptions()
  const range = rangeFor(when)
  const params = { q, status, className, section, subjectName, teacherId, from: range.from, to: range.to, page, limit: 25 }
  const { data, isLoading, isFetching } = useLessonPlans(params)
  const save = useSaveLessonPlan()
  const setPlanStatus = useLessonPlanStatus()
  const remove = useDeleteLessonPlan()

  useEffect(() => setPage(1), [q, status, className, section, subjectName, teacherId, when])

  useEffect(() => {
    const openId = searchParams.get('open')
    const isNew = searchParams.get('new') === '1'
    if (!openId && !isNew) return
    searchParams.delete('open')
    searchParams.delete('new')
    setSearchParams(searchParams, { replace: true })
    if (isNew) {
      setDraft(emptyDraft())
      setErrors({})
      setSheetOpen(canWrite)
      return
    }
    acdmcService
      .getLessonPlan(openId as string)
      .then((plan) => {
        setDraft(toDraft(plan))
        setErrors({})
        setSheetOpen(true)
      })
      .catch((error) => toast.error(errorMessage(error, "Couldn't open that lesson plan.")))
  }, [searchParams, setSearchParams, canWrite])

  const openCreate = () => {
    setDraft({ ...emptyDraft(), className, section, subjectName })
    setErrors({})
    setSheetOpen(true)
  }
  const openEdit = (plan: LessonPlan) => {
    setDraft(toDraft(plan))
    setErrors({})
    setSheetOpen(true)
  }
  const openDuplicate = (plan: LessonPlan) => {
    const copy = toDraft(plan)
    setDraft({ ...copy, _id: undefined, status: 'draft', section: '', teacherId: '', teacherName: '' })
    setErrors({})
    setSheetOpen(true)
    toast('Choose the section for the copy.')
  }

  const changeStatus = async (plan: LessonPlan, next: LessonPlanStatus, message: string) => {
    try {
      await setPlanStatus.mutateAsync({ id: plan._id, status: next })
      toast.success(message)
    } catch (error) {
      toast.error(errorMessage(error))
    }
  }

  const submit = async (nextStatus?: LessonPlanStatus) => {
    const next: typeof errors = {}
    if (!draft.title.trim()) next.title = 'Add a title'
    if (!draft.className) next.className = 'Choose a class'
    if (!draft.section) next.section = 'Choose a section'
    if (!draft.subjectName.trim()) next.subjectName = 'Choose a subject'
    if (!draft.date) next.date = 'Choose the date of the lesson'
    setErrors(next)
    if (Object.keys(next).length) {
      toast.error(Object.values(next)[0] as string)
      return
    }
    const { _id, status: current, structured, sequenceIndex, sequenceTotal, isSupplementary, ...payload } = draft
    const status = nextStatus || (current === 'draft' || current === 'planned' ? current : undefined)
    const body = {
      ...payload,
      subjectName: payload.subjectName.trim(),
      structured: {
        ...structured,
        learningObjectives: { general: payload.objectives },
        teachingProcess: {
          ...structured.teachingProcess,
          teacherActivities: payload.activities,
        },
        assessmentChecks: { evidenceNotes: payload.assessment },
        homework: { ...structured.homework, tasks: structured.homework.tasks || payload.assessment },
      },
      sequencePart: { index: sequenceIndex, total: sequenceTotal },
      isSupplementary,
      ...(status ? { status } : {}),
    }
    try {
      await save.mutateAsync({ id: _id, payload: body })
      toast.success(_id ? 'Lesson plan updated.' : status === 'planned' ? 'Lesson plan saved as planned.' : 'Lesson plan saved as a draft.')
      setSheetOpen(false)
    } catch (error) {
      toast.error(errorMessage(error, "Couldn't save the lesson plan."))
    }
  }

  const markDelivered = async () => {
    if (!deliverTarget) return
    try {
      await setPlanStatus.mutateAsync({ id: deliverTarget._id, status: 'delivered', deliveryNote, deliveredOn })
      toast.success('Marked as delivered.')
      setDeliverTarget(null)
    } catch (error) {
      toast.error(errorMessage(error))
    }
  }

  const runDelete = async () => {
    if (!deleteTarget) return
    try {
      await remove.mutateAsync(deleteTarget._id)
      toast.success('Lesson plan deleted.')
      setDeleteTarget(null)
    } catch (error) {
      toast.error(errorMessage(error, "Couldn't delete the lesson plan."))
    }
  }

  const rowMenu = (plan: LessonPlan): MenuItem[] => {
    if (!canWrite) {
      return canDelete ? [{ label: 'Delete', icon: Trash2, tone: 'danger', onSelect: () => setDeleteTarget(plan) }] : []
    }
    const items: MenuItem[] = [{ label: 'Edit', icon: Pencil, onSelect: () => openEdit(plan) }]
    if (plan.status === 'draft' || plan.status === 'planned') {
      items.push({
        label: 'Mark as delivered',
        icon: CalendarCheck,
        onSelect: () => {
          setDeliveryNote('')
          setDeliveredOn(plan.date && plan.date <= todayKey() ? plan.date : todayKey())
          setDeliverTarget(plan)
        },
      })
    }
    if (plan.status === 'draft') items.push({ label: 'Mark as planned', icon: Send, onSelect: () => changeStatus(plan, 'planned', 'Marked as planned.') })
    if (plan.status === 'delivered') items.push({ label: 'Undo delivered', icon: RotateCcw, onSelect: () => changeStatus(plan, 'planned', 'Moved back to planned.') })
    if (plan.status === 'cancelled') items.push({ label: 'Restore as planned', icon: RotateCcw, onSelect: () => changeStatus(plan, 'planned', 'Lesson plan restored.') })
    if (plan.status === 'draft' || plan.status === 'planned') {
      items.push({ label: 'Cancel lesson', icon: Ban, onSelect: () => changeStatus(plan, 'cancelled', 'Lesson cancelled.') })
    }
    items.push({
      label: 'Duplicate (new record)',
      icon: Copy,
      onSelect: () =>
        duplicatePlan
          .mutateAsync(plan._id)
          .then(() => toast.success('Duplicated as a new draft'))
          .catch((err) => toast.error(errorMessage(err, 'Duplicate failed'))),
    })
    items.push({ label: 'Duplicate for another section', icon: Copy, onSelect: () => openDuplicate(plan) })
    if (canDelete) items.push({ divider: true }, { label: 'Delete', icon: Trash2, tone: 'danger', onSelect: () => setDeleteTarget(plan) })
    return items
  }

  const columns = useMemo<DataTableColumn<LessonPlan>[]>(
    () => [
      {
        key: 'date',
        header: 'Date',
        width: 110,
        mobile: 'subtitle',
        render: (p) => (
          <Entity
            primary={shortDate(p.date)}
            secondary={p.date ? `${WEEKDAYS[new Date(`${p.date}T00:00:00`).getDay()]} · ${p.periods} ${p.periods === 1 ? 'period' : 'periods'}` : undefined}
          />
        ),
      },
      {
        key: 'title',
        header: 'Lesson',
        mobile: 'title',
        wrap: true,
        render: (p) => (
          <Entity
            primary={p.title}
            secondary={
              p.topicIds.length
                ? `${p.topicIds.length} syllabus ${p.topicIds.length === 1 ? 'topic' : 'topics'}${p.topic ? ` · ${p.topic}` : ''}`
                : p.topic || undefined
            }
          />
        ),
      },
      { key: 'class', header: 'Class', render: (p) => classLabel(p.className, p.section) },
      { key: 'subject', header: 'Subject', render: (p) => p.subjectName || '—' },
      { key: 'teacher', header: 'Teacher', mobile: 'hidden', render: (p) => p.teacherName || '—' },
      { key: 'status', header: 'Status', mobile: 'aside', render: (p) => <StatusBadge status={p.status} /> },
      ...(canWrite || canDelete
        ? [
            {
              key: 'actions',
              header: '',
              headerLabel: 'Actions',
              interactive: true,
              width: 48,
              render: (p: LessonPlan) => <Menu items={rowMenu(p)} label={`Actions for ${p.title}`} />,
            },
          ]
        : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canDelete, canWrite]
  )

  const counts = data?.counts || { draft: 0, planned: 0, delivered: 0, cancelled: 0 }
  const total = counts.draft + counts.planned + counts.delivered + counts.cancelled
  const items = data?.items || []
  const pagination = data?.pagination
  const hasFilters = Boolean(q || status || className || section || subjectName || teacherId || when)
  const editingDelivered = draft._id && (draft.status === 'delivered' || draft.status === 'cancelled')

  return (
    <Page
      title="Lesson planning"
      description="Prepare structured lesson plans from published syllabi and academic plans. Scheduled periods come from the timetable — not recreated here."
    >
      {lpDashboard && (
        <div className="grid gap-4 md:grid-cols-4 mb-4">
          <Card className="p-4">
            <div className="text-sm text-muted-foreground">Drafts</div>
            <div className="text-lg font-semibold">{lpDashboard.counts.draft ?? 0}</div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-muted-foreground">Awaiting review</div>
            <div className="text-lg font-semibold">
              {(lpDashboard.counts.submitted ?? 0) + (lpDashboard.counts.under_review ?? 0)}
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-muted-foreground">Ready to teach</div>
            <div className="text-lg font-semibold">{lpDashboard.counts.planned ?? 0}</div>
          </Card>
          <Card className="p-4">
            <div className="text-sm text-muted-foreground">Upcoming</div>
            <div className="text-lg font-semibold">{lpDashboard.upcoming?.length ?? 0}</div>
          </Card>
        </div>
      )}

      <Card flush>
        <CardToolbar>
          <FilterBar
            search={{ value: q, onChange: setQ, placeholder: 'Search lesson, topic or teacher' }}
            chips={[
              { id: '', label: 'All', count: total, active: !status },
              { id: 'planned', label: 'Planned', count: counts.planned, active: status === 'planned' },
              { id: 'delivered', label: 'Delivered', count: counts.delivered, active: status === 'delivered' },
              { id: 'draft', label: 'Draft', count: counts.draft, active: status === 'draft' },
              { id: 'cancelled', label: 'Cancelled', count: counts.cancelled, active: status === 'cancelled' },
            ]}
            onChipClick={(id) => setStatus(id as LessonPlanStatus | '')}
            filters={
              <div className="ac-filters">
                <Select
                  size="sm"
                  aria-label="When"
                  options={[
                    { value: '', label: 'Any date' },
                    { value: 'today', label: 'Today' },
                    { value: 'week', label: 'This week' },
                    { value: 'upcoming', label: 'Upcoming' },
                    { value: 'past', label: 'Past' },
                  ]}
                  value={when}
                  onChange={(e) => setWhen(e.target.value as When)}
                />
                <Select
                  size="sm"
                  aria-label="Class"
                  placeholder="All classes"
                  options={classSelectOptions(options)}
                  value={className}
                  onChange={(e) => {
                    setClassName(e.target.value)
                    setSection('')
                  }}
                />
                {className ? (
                  <Select
                    size="sm"
                    aria-label="Section"
                    placeholder="All sections"
                    options={sectionSelectOptions(options, className)}
                    value={section}
                    onChange={(e) => setSection(e.target.value)}
                  />
                ) : null}
                <Select
                  size="sm"
                  aria-label="Subject"
                  placeholder="All subjects"
                  options={allSubjectNames(options)}
                  value={subjectName}
                  onChange={(e) => setSubjectName(e.target.value)}
                />
                {options?.teachers.length ? (
                  <Select
                    size="sm"
                    aria-label="Teacher"
                    placeholder="All teachers"
                    options={options.teachers.map((t) => ({ value: t._id, label: t.name }))}
                    value={teacherId}
                    onChange={(e) => setTeacherId(e.target.value)}
                  />
                ) : null}
              </div>
            }
            actions={
              canWrite && (
                <Button variant="primary" size="sm" icon={Plus} onClick={openCreate}>
                  Write lesson plan
                </Button>
              )
            }
          />
        </CardToolbar>
        <DataTable
          caption="Lesson plans"
          columns={columns}
          rows={items}
          getRowKey={(p) => p._id}
          onRowClick={openEdit}
          loading={isLoading || (isFetching && !items.length)}
          loadingLabel="Loading lesson plans"
          empty={
            hasFilters ? (
              <EmptyState icon={SearchX} title="No lesson plans match these filters" body="Clear a filter or search for something else." />
            ) : (
              <EmptyState
                icon={BookOpenText}
                title="No lesson plans yet"
                body={canWrite ? "Plan each lesson against the syllabus. Marking it delivered updates the section's syllabus coverage." : READ_ONLY_NOTE.classwork}
                action={
                  canWrite && (
                    <Button variant="primary" icon={Plus} onClick={openCreate}>
                      Write lesson plan
                    </Button>
                  )
                }
              />
            )
          }
          footer={
            pagination && pagination.total > 0 ? (
              <Pagination page={pagination.page} pages={pagination.pages} total={pagination.total} limit={pagination.limit} onPageChange={setPage} />
            ) : undefined
          }
        />
      </Card>

      <Sheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        size="lg"
        icon={BookOpenText}
        title={!canWrite ? 'Lesson plan' : draft._id ? 'Edit lesson plan' : 'Write lesson plan'}
        description={!canWrite ? READ_ONLY_NOTE.classwork : draft._id ? undefined : 'Save as a draft while you work, or as planned when it is ready.'}
        onSubmit={(event) => {
          event.preventDefault()
          if (canWrite) submit(draft._id ? undefined : 'planned')
        }}
        footer={
          !canWrite ? (
            <Button onClick={() => setSheetOpen(false)}>Close</Button>
          ) : (
          <>
            <Button onClick={() => setSheetOpen(false)}>Cancel</Button>
            {!draft._id || draft.status === 'draft' ? (
              <Button loading={save.isPending && !draft._id} onClick={() => submit('draft')}>
                Save as draft
              </Button>
            ) : null}
            {draft._id && draft.status === 'draft' ? (
              <Button variant="primary" loading={save.isPending} onClick={() => submit('planned')}>
                Save as planned
              </Button>
            ) : (
              <Button type="submit" variant="primary" loading={save.isPending}>
                {draft._id ? 'Save changes' : 'Save as planned'}
              </Button>
            )}
          </>
          )
        }
      >
        <fieldset className="ac-fieldset" disabled={!canWrite}>
        <div className="cb-form-section">
          <h3 className="cb-form-section__title">Class and subject</h3>
          <ClassContextFields
            value={draft}
            errors={errors}
            onChange={(patch) =>
              setDraft((d) => ({
                ...d,
                ...patch,
                ...(patch.className !== undefined || patch.subjectName !== undefined ? { curriculumId: '', topicIds: [] } : {}),
              }))
            }
          />
        </div>

        <div className="cb-form-section">
          <h3 className="cb-form-section__title">Lesson</h3>
          <div className="cb-form-grid cb-form-grid--3">
            <TextField
              className="is-wide"
              label="Title"
              required
              placeholder="e.g. Adding fractions with unlike denominators"
              value={draft.title}
              error={errors.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            />
            <TextField
              label="Date"
              type="date"
              required
              value={draft.date}
              error={errors.date}
              onChange={(e) => setDraft({ ...draft, date: e.target.value })}
            />
            <TextField
              label="Periods"
              type="number"
              min={1}
              max={20}
              value={String(draft.periods)}
              onChange={(e) => setDraft({ ...draft, periods: Math.min(20, Math.max(1, Number(e.target.value) || 1)) })}
            />
          </div>
        </div>

        <div className="cb-form-section">
          <h3 className="cb-form-section__title">What's covered</h3>
          <TopicPicker
            className={draft.className}
            section={draft.section}
            subjectName={draft.subjectName}
            curriculumId={draft.curriculumId}
            topicIds={draft.topicIds}
            onChange={(value) => setDraft((d) => ({ ...d, ...value }))}
          />
          <TextField
            label="Other topic"
            hint="Anything not in the syllabus, or the topic in your own words."
            value={draft.topic}
            onChange={(e) => setDraft({ ...draft, topic: e.target.value })}
          />
        </div>

        <div className="cb-form-section">
          <h3 className="cb-form-section__title">Plan</h3>
          <TextArea label="Learning objectives" rows={3} value={draft.objectives} onChange={(e) => setDraft({ ...draft, objectives: e.target.value })} />
          <TextArea label="Classroom activities" rows={4} value={draft.activities} onChange={(e) => setDraft({ ...draft, activities: e.target.value })} />
          <TextArea label="Resources" rows={2} hint="Books, pages, worksheets, links." value={draft.resources} onChange={(e) => setDraft({ ...draft, resources: e.target.value })} />
          <TextArea label="How you'll check learning" rows={2} value={draft.assessment} onChange={(e) => setDraft({ ...draft, assessment: e.target.value })} />
        </div>

        {planContext.data?.academic?.plan ? (
          <div className="cb-form-section rounded-md bg-muted/40 p-3 text-sm">
            <div className="font-medium">Academic plan context</div>
            <p className="text-muted-foreground mt-1">
              Plan status {(planContext.data.academic.plan as { status?: string }).status} ·{' '}
              {((planContext.data.academic.allocations as unknown[] | undefined) || []).length} allocation row(s) for this topic window.
            </p>
          </div>
        ) : null}

        <div className="cb-form-section">
          <h3 className="cb-form-section__title">Teaching sequence</h3>
          <div className="cb-form-grid cb-form-grid--3">
            <TextField
              label="Part"
              type="number"
              min={1}
              value={String(draft.sequenceIndex)}
              onChange={(e) => setDraft({ ...draft, sequenceIndex: Number(e.target.value) || 1 })}
            />
            <TextField
              label="Of"
              type="number"
              min={1}
              value={String(draft.sequenceTotal)}
              onChange={(e) => setDraft({ ...draft, sequenceTotal: Number(e.target.value) || 1 })}
            />
            <label className="flex items-center gap-2 text-sm mt-6">
              <input
                type="checkbox"
                checked={draft.isSupplementary}
                onChange={(e) => setDraft({ ...draft, isSupplementary: e.target.checked })}
              />
              Supplementary / unscheduled
            </label>
          </div>
        </div>

        <div className="cb-form-section">
          <h3 className="cb-form-section__title">Teaching-learning process</h3>
          <TextArea
            label="Introduction"
            rows={2}
            value={draft.structured.teachingProcess.introduction}
            onChange={(e) =>
              setDraft({
                ...draft,
                structured: {
                  ...draft.structured,
                  teachingProcess: { ...draft.structured.teachingProcess, introduction: e.target.value },
                },
              })
            }
          />
          <TextArea
            label="Methodology"
            rows={2}
            hint="Lecture, demonstration, inquiry, collaborative, etc."
            value={draft.structured.teachingProcess.methodology}
            onChange={(e) =>
              setDraft({
                ...draft,
                structured: {
                  ...draft.structured,
                  teachingProcess: { ...draft.structured.teachingProcess, methodology: e.target.value },
                },
              })
            }
          />
          <TextArea
            label="Summary"
            rows={2}
            value={draft.structured.teachingProcess.summary}
            onChange={(e) =>
              setDraft({
                ...draft,
                structured: {
                  ...draft.structured,
                  teachingProcess: { ...draft.structured.teachingProcess, summary: e.target.value },
                },
              })
            }
          />
        </div>

        <div className="cb-form-section">
          <h3 className="cb-form-section__title">Differentiation & follow-up</h3>
          <TextArea
            label="Support strategies"
            rows={2}
            value={draft.structured.differentiated.support}
            onChange={(e) =>
              setDraft({
                ...draft,
                structured: {
                  ...draft.structured,
                  differentiated: { ...draft.structured.differentiated, support: e.target.value },
                },
              })
            }
          />
          <TextArea
            label="Extension activities"
            rows={2}
            value={draft.structured.differentiated.extension}
            onChange={(e) =>
              setDraft({
                ...draft,
                structured: {
                  ...draft.structured,
                  differentiated: { ...draft.structured.differentiated, extension: e.target.value },
                },
              })
            }
          />
          <TextArea
            label="Homework / follow-up"
            rows={2}
            value={draft.structured.homework.tasks}
            onChange={(e) =>
              setDraft({
                ...draft,
                structured: {
                  ...draft.structured,
                  homework: { ...draft.structured.homework, tasks: e.target.value },
                },
              })
            }
          />
          <TextArea
            label="Teacher reflection"
            rows={2}
            value={draft.structured.reflection.anticipatedDifficulties}
            onChange={(e) =>
              setDraft({
                ...draft,
                structured: {
                  ...draft.structured,
                  reflection: { ...draft.structured.reflection, anticipatedDifficulties: e.target.value },
                },
              })
            }
          />
        </div>

        {editingDelivered ? (
          <p className="cb-muted ac-note">
            This lesson is {draft.status}. Use the row's actions to change its status.
          </p>
        ) : null}
        </fieldset>
      </Sheet>

      <Dialog
        open={Boolean(deliverTarget)}
        onClose={() => setDeliverTarget(null)}
        title="Mark lesson as delivered"
        description={deliverTarget ? `${deliverTarget.title} · ${classLabel(deliverTarget.className, deliverTarget.section)}` : undefined}
        footer={
          <>
            <Button onClick={() => setDeliverTarget(null)}>Cancel</Button>
            <Button variant="primary" icon={CalendarCheck} loading={setPlanStatus.isPending} onClick={markDelivered}>
              Mark as delivered
            </Button>
          </>
        }
      >
        <div className="cb-form-section">
          <TextField label="Delivered on" type="date" max={todayKey()} value={deliveredOn} onChange={(e) => setDeliveredOn(e.target.value)} />
          <TextArea
            label="How it went"
            hint="Optional. What was finished, what carries over."
            rows={3}
            value={deliveryNote}
            onChange={(e) => setDeliveryNote(e.target.value)}
          />
        </div>
      </Dialog>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        tone="danger"
        title="Delete this lesson plan?"
        description={deleteTarget ? `${deleteTarget.title} will be removed and no longer count towards syllabus coverage.` : undefined}
        footer={
          <>
            <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="danger" loading={remove.isPending} onClick={runDelete}>
              Delete lesson plan
            </Button>
          </>
        }
      />
    </Page>
  )
}

export default AcdmcLessonPlan
